import React from "react";
import { MagnifyingGlassCircleIcon } from "@heroicons/react/24/outline";
import Button from "@/components/ui/Button";
import { MARKET_COPY } from "@/lib/market/copy";

export type MarketEmptyVariant = "error" | "search" | "filtered" | "plain";

interface MarketEmptyStateProps {
  variant: MarketEmptyVariant;
  searchQuery?: string;
  /** Whether related suggestions exist (search variant only). */
  hasRelated?: boolean;
  /** Related shelf, rendered between the headline and the recovery CTA. */
  relatedSlot?: React.ReactNode;
  onRetry: () => void;
  onClearFilters: () => void;
  onRequestItem: () => void;
}

/**
 * Market empty & error states (Phase 5, C6/C7).
 *
 * Every variant leads with what happened, then offers ONE dominant recovery
 * action. For a zero-result search the layout is headline → related shelf →
 * request CTA, so the copy ("related items…") always matches what's on screen.
 */
const MarketEmptyState = React.memo(
  ({
    variant,
    searchQuery = "",
    hasRelated = false,
    relatedSlot,
    onRetry,
    onClearFilters,
    onRequestItem,
  }: MarketEmptyStateProps) => {
    const requestCta = (
      <div className="space-y-4">
        <p className="text-text-light">{MARKET_COPY.requestItemPrompt}</p>
        <Button
          variant="secondary"
          onClick={onRequestItem}
          className="inline-flex items-center gap-2 px-6 py-3"
        >
          <MagnifyingGlassCircleIcon className="h-5 w-5" aria-hidden="true" />
          {MARKET_COPY.requestItemCta}
        </Button>
        <p className="text-sm text-text-light">
          {MARKET_COPY.requestItemHelper}
        </p>
      </div>
    );

    if (variant === "error") {
      return (
        <div className="text-center py-12" role="alert">
          <p className="text-red-600 font-medium mb-2">
            {MARKET_COPY.errorTitle}
          </p>
          <p className="text-text-light mb-6">{MARKET_COPY.errorBody}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button variant="primary" onClick={onRetry} className="px-6 py-3">
              {MARKET_COPY.errorRetryCta}
            </Button>
            <button
              onClick={onClearFilters}
              className="text-primary hover:underline"
            >
              {MARKET_COPY.errorBrowseAllCta}
            </button>
          </div>
        </div>
      );
    }

    if (variant === "search") {
      const title = (
        hasRelated
          ? MARKET_COPY.searchEmptyWithRelatedTitle
          : MARKET_COPY.searchEmptyTitle
      ).replace("{q}", searchQuery);

      return (
        <div className="py-12">
          <p className="text-center text-foreground font-medium mb-8">
            {title}
          </p>
          {hasRelated && relatedSlot ? (
            <div className="mb-10">{relatedSlot}</div>
          ) : null}
          <div className="text-center">{requestCta}</div>
        </div>
      );
    }

    if (variant === "filtered") {
      return (
        <div className="text-center py-12">
          <p className="text-foreground font-medium mb-2">
            {MARKET_COPY.filteredEmptyTitle}
          </p>
          <p className="text-text-light mb-6">
            {MARKET_COPY.filteredEmptyBody}
          </p>
          <div className="space-y-4">
            <Button
              variant="primary"
              onClick={onClearFilters}
              className="px-6 py-3"
            >
              {MARKET_COPY.clearFiltersCta}
            </Button>
            <div className="pt-2">
              <button
                onClick={onRequestItem}
                className="text-primary hover:underline text-sm"
              >
                {MARKET_COPY.requestItemCta}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <p className="text-text-light mb-6">{MARKET_COPY.plainEmptyTitle}</p>
        {requestCta}
      </div>
    );
  },
);
MarketEmptyState.displayName = "MarketEmptyState";

export default MarketEmptyState;
